const API_ENDPOINT = import.meta.env.VITE_APP_REST_API_ENDPOINT as string;
const API_REGEXP = /^(https?):\/\/(.+)$/;
const [, API_SCHEMA, API_HOST] = API_REGEXP.exec(API_ENDPOINT)!;
const TOKEN_URL = `${API_ENDPOINT}/oauth/v2/token`;

export const API_V1_SWAGGER_SPEC = {
    swagger: "2.0",
    host: API_HOST,
    schemes: [API_SCHEMA],
    info: {
        title: "IrriWatch API V1",
        description:
            "\u003Cul\u003E \u003Cli\u003EAccess to the API is granted for every user by accessing their personal API credentials under their account name.\u003C/li\u003E \u003Cli\u003EAccess keys are secrets and \u003Cstrong\u003EMUST NOT\u003C/strong\u003E be shared with third parties nor be shared via email. If your access keys have been misused, you should generate new credentials.\u003C/li\u003E \u003Cli\u003ECalls to the API may be blocked when many calls are being performed in a given moment.\u003C/li\u003E \u003Cli\u003EResources are generally only 7 days available for download after generation\u003C/li\u003E \u003Cli\u003ESoftware using the API must be able to follow redirects (http code 302)\u003C/li\u003E \u003Cli\u003E\u003Cstrong\u003E\u003Cspan style='color:red'\u003EPlease note all calls to this API are logged. Abuse of the API may result in removal of access rights\u003C/span\u003E\u003C/strong\u003E\u003C/li\u003E \u003C/ul\u003E",
        version: "-",
    },
    paths: {
        "/api/v1/company": {
            get: {
                responses: {
                    "200": {
                        description: "List all companies",
                        schema: {
                            items: {
                                $ref: "#/definitions/CompanyResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Returns the details of a company",
                        schema: {
                            $ref: "#/definitions/CompanyResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/user": {
            post: {
                summary: "Create a user as a company manager",
                description:
                    "Requires a user OAuth token obtained from /oauth/v2/token using a manager's personal API credentials (client_credentials grant). " +
                    "The user must be active, API-enabled, and a MANAGER of this company or a parent company. " +
                    "Global ADMIN/CS roles alone and company/service tokens do not grant access. " +
                    "The company UUID establishes authorization only: the new user has global role USER and no company membership. " +
                    "A password is generated and the existing welcome email with login credentials is triggered. " +
                    "No password, elevated permissions, or email-suppression options are accepted. " +
                    "Membership and farm/field setup are handled separately. Repeating a successful request returns 400 for the existing email.",
                parameters: [
                    {name: "companyUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "user", in: "body", required: true, schema: {$ref: "#/definitions/CreateUserRequest"}},
                ],
                responses: {
                    "201": {
                        description: "User created and welcome email triggered. Email delivery is asynchronous.",
                        schema: {$ref: "#/definitions/CreateUserResponse"},
                    },
                    "400": {
                        description:
                            "Invalid body, query parameters, or company UUID; or User with given email already exists.",
                        schema: {$ref: "#/definitions/CreateUserError"},
                    },
                    "401": {
                        description:
                            "Missing, invalid, or expired OAuth token; returned by the existing OAuth middleware.",
                    },
                    "403": {
                        description:
                            "Caller is not an active, API-enabled user with manager access to the specified company.",
                        schema: {$ref: "#/definitions/CreateUserError"},
                    },
                    "404": {description: "Company not found.", schema: {$ref: "#/definitions/CreateUserError"}},
                    "500": {
                        description:
                            "User creation failed. Investigate the failure before retrying; Cognito and database changes are not atomic.",
                        schema: {$ref: "#/definitions/CreateUserError"},
                    },
                },
            },
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "List all users",
                        schema: {
                            items: {
                                $ref: "#/definitions/UserResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order": {
            get: {
                parameters: [
                    {
                        name: "active",
                        in: "query",
                        required: false,
                        description:
                            "Returns inactive orders if false, active orders if true, all orders if not provided",
                        type: "boolean",
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Lists all orders for a company",
                        schema: {
                            items: {
                                $ref: "#/definitions/OrderResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
            post: {
                parameters: [
                    {
                        name: "order",
                        in: "body",
                        description:
                            "Create new order. Each Field Feature may include optional top-level `metadata`; supplying it, even as {}, requires either an active, API-enabled user with MANAGER membership in this company or a parent company, or a company OAuth token for this company or a parent company. Omitting Field metadata preserves existing creation permissions and behaviour. `start_date` is optional; if omitted, the API uses the current date. When provided, use the YYYY-MM-DD format. Each field feature may also include an optional `properties.planting_date` in YYYY-MM-DD format; if omitted, it defaults to the order start date.",
                        schema: {
                            $ref: "#/definitions/OrderRequest",
                        },
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Returns the order created",
                        schema: {
                            $ref: "#/definitions/OrderResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/fields/{fieldUuid}": {
            patch: {
                summary: "Update an Order Field's properties and/or metadata",
                description:
                    "Requires either an active, API-enabled user OAuth token with MANAGER membership in this company or a parent company, or a company OAuth token for this company or a parent company. Only the existing API v1 Field properties and metadata can change; UUID, Company, Order, Farm, and geometry cannot. Supplied metadata keys are upserted and omitted keys are preserved.",
                parameters: [
                    {name: "companyUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "orderUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "fieldUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {
                        name: "update",
                        in: "body",
                        required: true,
                        schema: {$ref: "#/definitions/PatchOrderFieldRequest"},
                    },
                ],
                responses: {
                    "200": {
                        description: "Updated GeoJSON Feature with its UUID, name, and populated metadata.",
                        schema: {$ref: "#/definitions/OrderFieldFeature"},
                    },
                    "400": {description: "Invalid identifier, property, metadata key, value, or limit."},
                    "401": {description: "Missing, invalid, or expired OAuth token."},
                    "403": {description: "Caller is not an active, API-enabled company manager."},
                    "404": {description: "Company, accessible Order, or Field belonging to that Order was not found."},
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/fields/{fieldUuid}/metadata/{key}": {
            delete: {
                summary: "Delete one Field metadata key",
                parameters: [
                    {name: "companyUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "orderUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "fieldUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "key", in: "path", required: true, type: "string", pattern: "^[A-Za-z][A-Za-z0-9_-]{0,63}$"},
                ],
                responses: {
                    "204": {description: "Key deleted; no response body."},
                    "400": {description: "Invalid identifier or metadata key."},
                    "401": {description: "Missing, invalid, or expired OAuth token."},
                    "403": {description: "Caller is not an active, API-enabled company manager."},
                    "404": {description: "Company, accessible Order, Field, or metadata key was not found."},
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/fields/{fieldUuid}/metadata": {
            delete: {
                summary: "Clear all metadata from one Field",
                parameters: [
                    {name: "companyUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "orderUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "fieldUuid", in: "path", required: true, type: "string", format: "uuid"},
                ],
                responses: {
                    "204": {description: "Metadata cleared; no response body."},
                    "401": {description: "Missing, invalid, or expired OAuth token."},
                    "403": {description: "Caller is not an active, API-enabled company manager."},
                    "404": {description: "Company, accessible Order, or Field belonging to that Order was not found."},
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/fields/metadata": {
            delete: {
                summary: "Clear metadata from every Field in an Order",
                parameters: [
                    {name: "companyUuid", in: "path", required: true, type: "string", format: "uuid"},
                    {name: "orderUuid", in: "path", required: true, type: "string", format: "uuid"},
                ],
                responses: {
                    "200": {
                        description: "Number of Fields whose metadata was cleared.",
                        schema: {$ref: "#/definitions/ClearFieldMetadataResponse"},
                    },
                    "401": {description: "Missing, invalid, or expired OAuth token."},
                    "403": {description: "Caller is not an active, API-enabled company manager."},
                    "404": {description: "Company or accessible Order was not found."},
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/complete": {
            post: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Empty response",
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Detailed order status",
                        schema: {
                            $ref: "#/definitions/OrderResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/meteo": {
            post: {
                parameters: [
                    {
                        name: "meteo_record",
                        in: "body",
                        required: true,
                        description:
                            "Create new meteo record. NOTE! Time zones are ignored. Period is interpreted as UTC",
                        schema: {
                            $ref: "#/definitions/ClimateRequest",
                        },
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Returns the meteo record created",
                        schema: {
                            $ref: "#/definitions/ClimateResponse",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/applied-irrigation": {
            post: {
                summary: "Record applied irrigation and/or precipitation for one or more fields",
                description:
                    "Post the irrigation amount (mm) and/or precipitation (mm) applied to one or more fields for a given date. " +
                    "Each field entry must provide at least one of `irrigation` or `precipitation`. " +
                    "A default `date` (YYYY-MM-DD) can be supplied at the top level and/or overridden per field entry. " +
                    "If a record already exists for the (field, date) pair, only the fields explicitly included in the request are updated; " +
                    "omitted fields keep their previously stored value (patch semantics). Otherwise a new record is created. " +
                    "The response reflects the persisted state after the upsert, so preserved values from prior requests are returned. " +
                    "Each field must belong to the requested company or one of its child companies within its hierarchy. " +
                    "Values are capped at 0 mm minimum and 999 mm maximum to match the portal's UI bounds.",
                parameters: [
                    {
                        name: "applied_irrigation_record",
                        in: "body",
                        required: true,
                        description: "Batch of applied irrigation records to create or update.",
                        schema: {
                            $ref: "#/definitions/AppliedIrrigationRequest",
                        },
                    },
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "201": {
                        description: "Returns the applied irrigation records that were created or updated",
                        schema: {
                            $ref: "#/definitions/AppliedIrrigationResponse",
                        },
                    },
                    "400": {
                        description:
                            "Invalid request (invalid body, missing values, duplicated entries in the batch, or a field not in the company hierarchy)",
                    },
                    "403": {
                        description: "Caller is not authorized to submit data for this company",
                    },
                    "404": {
                        description: "Company not found",
                    },
                    "409": {
                        description:
                            "A concurrent request already created a record for the same (field, date). Retry the request.",
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result/date/{date}": {
            get: {
                summary: "Retrieve order results as a ZIP file",
                description: "Returns a ZIP file containing order results for a specific company, order, and date.",
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                            format: "uuid",
                        },
                        description: "UUID of the company",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                            format: "uuid",
                        },
                        description: "UUID of the order",
                    },
                    {
                        name: "date",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                            format: "date",
                        },
                        description: "Date in YYYY-MM-DD format",
                    },
                ],
                responses: {
                    "200": {
                        description: "Returns a ZIP file containing order results",
                        content: {
                            "application/zip": {
                                schema: {
                                    type: "string",
                                    format: "binary",
                                },
                            },
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Lists all results ZIP for an order",
                        schema: {
                            items: {
                                $ref: "#/definitions/ResultResponse",
                            },
                            type: "array",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result/{resultUuid}": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "resultUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description: "Download result ZIP",
                        schema: {
                            type: "string",
                            format: "binary",
                        },
                    },
                },
            },
        },
        "/api/v1/company/{companyUuid}/order/{orderUuid}/result/{date}/field_level": {
            get: {
                parameters: [
                    {
                        name: "companyUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "orderUuid",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                    {
                        name: "date",
                        in: "path",
                        required: true,
                        type: "string",
                    },
                ],
                responses: {
                    "200": {
                        description:
                            "Download JSON field level parameter result, date must follow format YYYYMMDD, e.g. 5 februari 2021 is 20210205",
                        schema: {
                            type: "string",
                            format: "binary",
                        },
                    },
                },
            },
        },
        "/api/v1/reference/crop": {
            get: {
                responses: {
                    "200": {
                        description: "Crop coding list",
                    },
                },
            },
        },
        "/api/v1/reference/irrigation": {
            get: {
                responses: {
                    "200": {
                        description: "Irrigation coding list",
                    },
                },
            },
        },
        "/api/v1/reference/soil": {
            get: {
                responses: {
                    "200": {
                        description: "Soil coding list",
                    },
                },
            },
        },
    },
    definitions: {
        WindResponse: {
            required: ["average_speed"],
            properties: {
                average_speed: {
                    type: "number",
                    format: "float",
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        WindRequest: {
            required: ["average_speed"],
            properties: {
                average_speed: {
                    type: "number",
                    format: "float",
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        CreateUserRequest: {
            type: "object",
            additionalProperties: false,
            required: ["email", "first_name", "last_name", "enabled_marketing_emails"],
            properties: {
                email: {
                    type: "string",
                    format: "email",
                    maxLength: 200,
                    pattern: "^[^A-Z]+$",
                    description: "Lowercase email, following existing user validation.",
                },
                first_name: {type: "string", minLength: 1, maxLength: 200},
                last_name: {type: "string", minLength: 1, maxLength: 200},
                company: {
                    type: "string",
                    minLength: 1,
                    maxLength: 200,
                    "x-nullable": true,
                    description: "Optional profile text, not a company ID or membership. Defaults to null.",
                },
                country: {
                    type: "string",
                    minLength: 1,
                    maxLength: 200,
                    "x-nullable": true,
                    description: "Optional; defaults to null.",
                },
                phone: {
                    type: "string",
                    minLength: 1,
                    maxLength: 20,
                    "x-nullable": true,
                    description: "Optional; defaults to null.",
                },
                enabled_marketing_emails: {
                    type: "boolean",
                    description: "Explicit marketing email preference; does not suppress the account welcome email.",
                },
                language: {
                    type: "string",
                    enum: [
                        "ENGLISH",
                        "FRANCAIS",
                        "ESPANOL",
                        "NEDERLANDS",
                        "ITALIANO",
                        "DEUTSCH",
                        "ARABIC",
                        "HINDI",
                        "PORTUGUESE",
                    ],
                },
                measure_system: {type: "string", enum: ["IMPERIAL", "METRIC"]},
                date_format: {type: "string", enum: ["MM/DD/YYYY", "DD/MM/YYYY"]},
            },
            example: {
                email: "farmer@example.com",
                first_name: "Ana",
                last_name: "Lopez",
                country: "Mexico",
                enabled_marketing_emails: false,
                language: "ESPANOL",
                measure_system: "METRIC",
            },
        },
        CreateUserResponse: {
            type: "object",
            required: ["message", "uuid"],
            properties: {
                message: {type: "string"},
                uuid: {type: "string", format: "uuid"},
            },
            example: {message: "User created successfully.", uuid: "461628d7-40f2-45ad-8770-0f46c54de2a9"},
        },
        CreateUserError: {
            type: "object",
            required: ["error"],
            properties: {error: {type: "string"}},
            example: {error: "User with given email already exists"},
        },
        UserResponse: {
            required: ["first_name", "last_name", "email"],
            properties: {
                first_name: {
                    type: "string",
                    maxLength: "200",
                },
                last_name: {
                    type: "string",
                    maxLength: "200",
                },
                email: {
                    type: "string",
                    maxLength: "200",
                },
                uuid: {type: "string"},
            },
            type: "object",
        },
        TemperatureResponse: {
            required: ["average"],
            properties: {
                min: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                max: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                average: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        TemperatureRequest: {
            required: ["average"],
            properties: {
                min: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                max: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                average: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        ResultResponse: {
            required: ["uuid", "name", "date"],
            properties: {
                uuid: {
                    type: "string",
                },
                name: {
                    type: "string",
                    maxLength: "200",
                },
                date: {
                    type: "string",
                },
            },
            type: "object",
        },
        ReferenceET0Response: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        ReferenceET0Request: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        PrecipitationResponse: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        PrecipitationRequest: {
            required: ["total"],
            properties: {
                total: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        PeriodResponse: {
            required: ["start", "end"],
            properties: {
                start: {
                    type: "string",
                    format: "date-time",
                },
                end: {
                    type: "string",
                    format: "date-time",
                },
            },
            type: "object",
        },
        PeriodRequest: {
            required: ["start", "end"],
            properties: {
                start: {
                    type: "string",
                    format: "date-time",
                },
                end: {
                    type: "string",
                    format: "date-time",
                },
            },
            type: "object",
        },
        FieldMetadata: {
            type: "object",
            maxProperties: 100,
            description:
                "Optional customer-defined reference values stored directly on this Field. Values are raw JSON strings, numbers, or booleans; nulls, arrays, and nested objects are not accepted. Up to 100 keys, each matching ^[A-Za-z][A-Za-z0-9_-]{0,63}$. Reserved keys constructor, prototype, and __proto__ are forbidden. Empty metadata is omitted from Field responses.",
            example: {
                FieldCode: "123",
                priority: 1,
                organic: false,
                review_date: "2026-09-25",
            },
        },
        OrderFieldProperties: {
            type: "object",
            properties: {
                uuid: {type: "string", format: "uuid", description: "IrriWatch-generated Field identifier."},
                name: {type: "string"},
            },
        },
        OrderFieldFeature: {
            type: "object",
            required: ["type", "geometry", "properties"],
            properties: {
                type: {type: "string", enum: ["Feature"]},
                geometry: {type: "object"},
                properties: {$ref: "#/definitions/OrderFieldProperties"},
                metadata: {$ref: "#/definitions/FieldMetadata"},
            },
        },
        PatchOrderFieldRequest: {
            type: "object",
            description:
                "Provide properties and/or metadata. Properties are partial. Metadata keys are upserted and omitted keys are preserved.",
            properties: {
                properties: {
                    type: "object",
                    properties: {
                        name: {type: "string"},
                        crop: {type: "string"},
                        soil: {type: "string"},
                        irrigation: {type: "string"},
                        planting_date: {type: "string", format: "date"},
                    },
                },
                metadata: {$ref: "#/definitions/FieldMetadata"},
            },
            example: {properties: {name: "Field 1 North", crop: "2"}, metadata: {FieldCode: "124", priority: 2}},
        },
        ClearFieldMetadataResponse: {
            type: "object",
            required: ["cleared_fields"],
            properties: {
                cleared_fields: {type: "integer", minimum: 0},
            },
        },
        OrderRequest: {
            required: ["fields"],
            properties: {
                fields: {
                    additionalProperties: true,
                    type: "object",
                    description:
                        "GeoJSON FeatureCollection. Each feature may include optional top-level metadata and optional properties.planting_date in YYYY-MM-DD format; if omitted, the order start date is used.",
                },
                start_date: {
                    type: "string",
                    format: "date",
                },
                subscription_type: {
                    type: "string",
                    maxLength: "200",
                },
            },
            example: {
                fields: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            geometry: {
                                type: "Polygon",
                                coordinates: [
                                    [
                                        [6.1, 49.6],
                                        [6.2, 49.6],
                                        [6.2, 49.7],
                                        [6.1, 49.7],
                                        [6.1, 49.6],
                                    ],
                                ],
                            },
                            properties: {
                                name: "Field 1",
                                crop: 1,
                                soil: 1,
                                irrigation: 1,
                                planting_date: "2026-06-15",
                            },
                            metadata: {
                                FieldCode: "123",
                                priority: 1,
                                organic: true,
                            },
                        },
                    ],
                },
                start_date: "2026-06-15",
                subscription_type: "standard",
            },
            type: "object",
        },
        OrderResponse: {
            required: ["fields"],
            properties: {
                uuid: {
                    type: "string",
                },
                id: {
                    type: "integer",
                },
                fields: {
                    type: "object",
                    description:
                        "GeoJSON FeatureCollection. Each Feature has properties.uuid and optional top-level metadata.",
                    properties: {
                        type: {
                            type: "string",
                            enum: ["FeatureCollection"],
                        },
                        features: {
                            type: "array",
                            items: {$ref: "#/definitions/OrderFieldFeature"},
                        },
                    },
                },
                state: {
                    type: "string",
                },
                subscription_type: {
                    type: "string",
                    maxLength: "200",
                },
            },
            example: {
                uuid: "c96c4d07-34a1-4f2c-9ee7-244c44efbdd1",
                id: 42,
                fields: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            geometry: {
                                type: "Polygon",
                                coordinates: [
                                    [
                                        [6.1, 49.6],
                                        [6.2, 49.6],
                                        [6.2, 49.7],
                                        [6.1, 49.7],
                                        [6.1, 49.6],
                                    ],
                                ],
                            },
                            properties: {
                                uuid: "b2a5994f-37a1-4d0b-9f30-93ed99b5dc3a",
                                name: "Field 1",
                            },
                            metadata: {
                                FieldCode: "123",
                                priority: 1,
                            },
                        },
                    ],
                },
                state: "pending",
                subscription_type: "standard",
            },
            type: "object",
        },
        LocationResponse: {
            required: ["lat", "lon"],
            properties: {
                lat: {
                    type: "number",
                    format: "float",
                },
                lon: {
                    type: "number",
                    format: "float",
                },
            },
            type: "object",
        },
        LocationRequest: {
            required: ["lat", "lon"],
            properties: {
                lat: {
                    type: "number",
                    format: "float",
                },
                lon: {type: "number", format: "float"},
            },
            type: "object",
        },
        HumidityResponse: {
            required: ["relative_average_in_percentage"],
            properties: {
                relative_average_in_percentage: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
                height: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
            },
            type: "object",
        },
        HumidityRequest: {
            required: ["relative_average_in_percentage"],
            properties: {
                relative_average_in_percentage: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
                height: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
            },
            type: "object",
        },
        CompanyResponse: {
            required: ["name", "uuid", "level"],
            properties: {
                name: {
                    type: "string",
                    maxLength: "200",
                },
                uuid: {
                    type: "string",
                },
                parentCompanyId: {
                    type: "string",
                    "x-nullable": true,
                },
                level: {
                    type: "number",
                },
            },
            example: {
                name: "Example Company",
                uuid: "805776ef-e1ee-46ec-b7ec-48d19c0a0e27",
                parentCompanyId: "bfb2990b-39f3-4f91-9c25-7f939106e519",
                level: 1,
            },
            type: "object",
        },
        ClimateResponse: {
            required: ["period", "location"],
            properties: {
                reference_et0: {$ref: "#/definitions/ReferenceET0Response"},
                period: {
                    $ref: "#/definitions/PeriodResponse",
                },
                precipitation: {
                    $ref: "#/definitions/PrecipitationResponse",
                },
                temperature: {
                    $ref: "#/definitions/TemperatureResponse",
                },
                humidity: {
                    $ref: "#/definitions/HumidityResponse",
                },
                wind: {
                    $ref: "#/definitions/WindResponse",
                },
                location: {
                    $ref: "#/definitions/LocationResponse",
                },
            },
            type: "object",
        },
        ClimateRequest: {
            required: ["period", "location"],
            properties: {
                reference_et0: {
                    $ref: "#/definitions/ReferenceET0Request",
                },
                period: {
                    $ref: "#/definitions/PeriodRequest",
                },
                precipitation: {
                    $ref: "#/definitions/PrecipitationRequest",
                },
                temperature: {
                    $ref: "#/definitions/TemperatureRequest",
                },
                humidity: {
                    $ref: "#/definitions/HumidityRequest",
                },
                wind: {
                    $ref: "#/definitions/WindRequest",
                },
                location: {
                    $ref: "#/definitions/LocationRequest",
                },
            },
            type: "object",
        },
        AppliedIrrigationRequest: {
            required: ["fields"],
            description:
                "Batch of applied irrigation entries. A default `date` can be provided at the top level and/or overridden per field entry. Each entry must include at least one of `irrigation` or `precipitation`.",
            properties: {
                date: {
                    type: "string",
                    format: "date",
                    description: "Default date (YYYY-MM-DD) used when a field entry does not carry its own `date`.",
                },
                fields: {
                    type: "array",
                    minItems: 1,
                    items: {
                        $ref: "#/definitions/AppliedIrrigationFieldRequest",
                    },
                },
            },
            example: {
                date: "2026-07-23",
                fields: [
                    {
                        field_id: "943872db-fd94-4a2a-a99e-12dd0697ff11",
                        irrigation: 12.5,
                        precipitation: 3.4,
                    },
                    {
                        field_id: "b360ffe5-628e-4c35-83fa-0f2402b20a08",
                        irrigation: 8,
                    },
                ],
            },
            type: "object",
        },
        AppliedIrrigationFieldRequest: {
            required: ["field_id"],
            properties: {
                field_id: {
                    type: "string",
                    format: "uuid",
                    description:
                        "UUID of the field the record applies to. The field must belong to the requested company or one of its child companies.",
                },
                date: {
                    type: "string",
                    format: "date",
                    description:
                        "Date (YYYY-MM-DD) for this field entry. Overrides the top-level `date` when both are provided.",
                },
                irrigation: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                    maximum: 999,
                    description: "Irrigation amount applied to the field, in millimetres (0-999).",
                },
                precipitation: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                    maximum: 999,
                    description: "Precipitation observed on the field, in millimetres (0-999).",
                },
            },
            type: "object",
        },
        AppliedIrrigationResponse: {
            required: ["fields"],
            properties: {
                fields: {
                    type: "array",
                    items: {
                        $ref: "#/definitions/AppliedIrrigationFieldResponse",
                    },
                },
            },
            type: "object",
        },
        AppliedIrrigationFieldResponse: {
            required: ["field_id", "date"],
            properties: {
                field_id: {
                    type: "string",
                    format: "uuid",
                },
                date: {
                    type: "string",
                    format: "date",
                },
                irrigation: {
                    type: "number",
                    format: "float",
                    "x-nullable": true,
                    description:
                        "Persisted irrigation amount (mm) after the upsert. May be a value preserved from a prior request, " +
                        "or null if no irrigation has ever been recorded for this (field, date).",
                },
                precipitation: {
                    type: "number",
                    format: "float",
                    "x-nullable": true,
                    description:
                        "Persisted precipitation amount (mm) after the upsert. May be a value preserved from a prior request, " +
                        "or null if no precipitation has ever been recorded for this (field, date).",
                },
            },
            type: "object",
        },
        AreaDTO: [],
        AreaResultDTO: [],
        TemperatureDTO: {
            required: ["average"],
            properties: {
                min: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                max: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                average: {
                    type: "number",
                    format: "float",
                    minimum: -273,
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
        HumidityDTO: {
            required: ["relative_average_in_percentage"],
            properties: {
                relative_average_in_percentage: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
                height: {
                    type: "number",
                    format: "float",
                    maximum: 100,
                    minimum: 0,
                },
            },
            type: "object",
        },
        WindDTO: {
            required: ["average_speed"],
            properties: {
                average_speed: {
                    type: "number",
                    format: "float",
                },
                height: {
                    type: "number",
                    format: "float",
                    minimum: 0,
                },
            },
            type: "object",
        },
    },
    securityDefinitions: {
        "Client Credentials": {
            name: "Authorization",
            type: "oauth2",
            flow: "application",
            tokenUrl: TOKEN_URL,
            description: "Provides an application flow access_token",
        },
    },
    security: [
        {
            "Client Credentials": [],
        },
    ],
};

export const API_V2_SWAGGER_SPEC = {
    swagger: "2.0",
    host: API_HOST,
    schemes: [API_SCHEMA],
    info: {
        title: "IrriWatch API V2",
        description:
            "Access to the API is granted for every user by accessing their personal API credentials under their account name",

        version: "-",
    },
    paths: {
        "/api/v2/company/{companyId}/madera-reports/download": {
            get: {
                summary: "Download a Madera report",
                parameters: [
                    {
                        name: "companyId",
                        in: "path",
                        required: true,
                        schema: {
                            type: "string",
                        },
                        description: "ID of the company",
                    },
                    {
                        name: "parcel",
                        in: "query",
                        schema: {
                            type: "string",
                        },
                    },
                    {
                        name: "startDate",
                        in: "query",
                        schema: {
                            type: "string",
                            format: "date",
                        },
                    },
                    {
                        name: "endDate",
                        in: "query",
                        schema: {
                            type: "string",
                            format: "date",
                        },
                    },
                    {
                        name: "reportType",
                        in: "query",
                        schema: {
                            type: "string",
                            enum: ["PARCEL", "FARM_UNIT"],
                        },
                    },
                    {
                        name: "farmUnit",
                        in: "query",
                        schema: {
                            type: "string",
                        },
                    },
                    {
                        name: "accountId",
                        in: "query",
                        required: false,
                        schema: {
                            type: "string",
                        },
                    },
                ],
                responses: {
                    "200": {
                        description: "Signed URL for downloading the report",
                        content: {
                            "application/json": {
                                schema: {
                                    type: "object",
                                    properties: {
                                        url: {
                                            type: "string",
                                            format: "uri",
                                        },
                                    },
                                },
                            },
                        },
                    },
                    "400": {
                        description: "Bad request",
                    },
                    "404": {
                        description: "Report not found",
                    },
                },
            },
        },
        "/api/v2/company/{companyId}/madera-reports": {
            post: {
                summary: "Upload Madera reports",
                consumes: ["multipart/form-data"],
                parameters: [
                    {
                        name: "companyId",
                        in: "path",
                        required: true,
                        type: "string",
                        description: "ID of the company",
                    },
                    {
                        name: "attachments",
                        in: "formData",
                        description: "File to upload",
                        required: true,
                        type: "file",
                    },
                ],
                responses: {
                    "200": {
                        description: "Upload results",
                        schema: {
                            type: "object",
                            properties: {
                                results: {
                                    type: "array",
                                    items: {
                                        type: "object",
                                        properties: {
                                            fileName: {type: "string"},
                                            status: {type: "string"},
                                        },
                                    },
                                },
                            },
                        },
                    },
                    "400": {
                        description: "Bad request",
                    },
                },
            },
        },
    },
    securityDefinitions: {
        "Client Credentials": {
            name: "Authorization",
            type: "oauth2",
            flow: "application",
            tokenUrl: TOKEN_URL,
            description: "Provides an application flow access_token",
        },
    },
    security: [
        {
            "Client Credentials": [],
        },
    ],
};
